import { createColumnHelper } from "@tanstack/react-table";
import Table from "components/ui/Table";
import { TableBreakpoint } from "components/ui/Table/interface";
import { getYear } from "date-fns";
import { NativeSelect, palette } from "lib/miolo";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";
import { useMemo, useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import type { IStockDividendsCorporateAction } from "types/graphqlTypes";

interface IAmericanStockDividendsPaidTabContetProps {
  data?: IStockDividendsCorporateAction[];
  loading: boolean;
}

export default function AmericanStockDividendsPaidTabContet({
  data,
  loading,
}: IAmericanStockDividendsPaidTabContetProps) {
  const [filterStockDividendsByYear, setFilterStockDividendsByYear] = useState<number>();
  const columnHelper = createColumnHelper<IStockDividendsCorporateAction>();

  const availableYears = useMemo(
    () => [
      ...new Set(
        data
          ?.map((dividend) => {
            if (dividend.excludingDate) {
              return getYear(parseDateYmd(dividend.excludingDate));
            }

            return undefined;
          })
          .filter(notUndefinedOrNull),
      ),
    ],
    [data],
  );

  if (!data && !filterStockDividendsByYear) {
    return null;
  }

  if (loading) {
    return <ClipLoader size={28} color={palette.royalBlue} loading={true} />;
  }

  const arrayOfColumns = [
    columnHelper.accessor("excludingDate", {
      header: "Conversion Date",
      cell: (props) => formatDateOrReturnHyphen(props.getValue()),
    }),
    columnHelper.accessor("sharePaid", {
      header: "Share Paid",
      cell: (props) => formatValue(props.getValue()),
      meta: {
        breakpoint: TableBreakpoint.Small,
      },
    }),
  ];

  const arrayOfDataFilteredByYear = data?.filter((dividend) => {
    if (!filterStockDividendsByYear) {
      return true;
    }

    if (getYear(parseDateYmd(dividend.excludingDate)) === filterStockDividendsByYear) {
      return true;
    }

    return false;
  });

  return (
    <div className="flex flex-col items-end space-y-2">
      <div className="w-full md:w-52">
        <NativeSelect
          height={48}
          value={filterStockDividendsByYear}
          onChange={(event) => setFilterStockDividendsByYear(Number(event.target.value))}
          style={{
            borderRadius: "8px",
          }}
        >
          <option value={undefined}>Filter By Year</option>
          {availableYears.map((year) => (
            <option key={`selectBoxYear/${year}`} value={year}>
              {year}
            </option>
          ))}
        </NativeSelect>
      </div>

      <Table<IStockDividendsCorporateAction>
        columns={arrayOfColumns}
        data={arrayOfDataFilteredByYear ?? []}
        isOddEven={true}
        isPaginationEnabled={true}
      />
    </div>
  );
}

function formatValue(value?: number | null) {
  if (!value) {
    return "-";
  }

  return numeral(value).format("0.000").replace(".000", "").replace(".", ",");
}
